import type { ReactElement } from 'react';
import {
  Button,
  Group,
  Paper,
  Stack,
  Text,
  useComputedColorScheme,
} from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import type { RuleTranslate } from '../shared/constants';
import { buttonTextColor, getOutlineControlProps } from 'utils/buttonUtils';

export const RuleEditHeader = ({
  translate,
  disabled,
  onDelete,
}: {
  translate: RuleTranslate;
  disabled: boolean;
  onDelete?: () => void;
}): ReactElement => {
  return (
    <Group justify="flex-end">
      <Group gap="xs">
        {onDelete && (
          <Button
            size="sm"
            variant="subtle"
            color="red"
            disabled={disabled}
            leftSection={<IconTrash size={16} />}
            onClick={onDelete}
          >
            {translate('pages:rules.builder.edit.delete')}
          </Button>
        )}
      </Group>
    </Group>
  );
};

export const RuleEditFooter = ({
  count,
  valid,
  saving,
  canModify,
  translate,
  onDiscard,
  onSave,
}: {
  count: number;
  valid: boolean;
  saving: boolean;
  canModify: boolean;
  translate: RuleTranslate;
  onDiscard: () => void;
  onSave: () => void;
}): ReactElement => {
  const colorScheme = useComputedColorScheme();
  const controlColor = colorScheme === 'dark' ? 'white' : 'dark';
  const saveDisabled = !valid || !canModify || saving;
  return (
    <Paper
      withBorder
      p="md"
      radius="lg"
      shadow="md"
      pos="sticky"
      bottom={0}
      bg="var(--mantine-color-body)"
      style={{ zIndex: 2 }}
      role="status"
    >
      <Group justify="space-between">
        <Stack gap={2}>
          <Text size="sm" fw={600}>
            {count}{' '}
            {translate(
              `pages:rules.builder.${
                count === 1 ? 'edit.unsavedChange' : 'edit.unsavedChanges'
              }`
            )}
          </Text>
          <Text size="xs" c="dimmed">
            {translate(
              `pages:rules.builder.${
                valid ? 'edit.saveHint' : 'edit.incomplete'
              }`
            )}
          </Text>
        </Stack>
        <Group gap="xs">
          <Button
            variant="subtle"
            size="sm"
            color={controlColor}
            c={buttonTextColor(colorScheme, saving)}
            disabled={saving}
            onClick={onDiscard}
          >
            {translate('pages:rules.builder.edit.discard')}
          </Button>
          <Button
            size="sm"
            {...getOutlineControlProps(colorScheme, saveDisabled)}
            disabled={saveDisabled}
            loading={saving}
            onClick={onSave}
            color={controlColor}
          >
            {translate('pages:rules.builder.edit.save')}
          </Button>
        </Group>
      </Group>
    </Paper>
  );
};
